// @ts-nocheck
import React from 'react';

import SwalToast from '../../components/Common/swalToast';
import { HAS_REFRESHED_KEY } from '../../const/CommonConst';

const handleRefresh = () => {
  sessionStorage.setItem(HAS_REFRESHED_KEY, 'true');
  window.location.reload();
};

/**
 * React Lazy wrapper function
 * Automatically reloads the window once if browser cannot fetch the corresponding chunk from the server.
 *
 * Why this?
 * After a deployment, the chunk files generated have a different hash from the last deployment.
 * If the platform is already open in the browser, the browser will not know about the latest deployment
 * and tries to fetch the old chunk. Since it cannot get it, the page goes blank.
 * @param {() => Promise<import('react').FC>} dynamicImportFn
 */
export const reactLazyRetry = (dynamicImportFn) =>
  React.lazy(() => {
    return new Promise((resolve, reject) => {
      const hasRefreshed = JSON.parse(
        sessionStorage.getItem(HAS_REFRESHED_KEY) || 'false'
      );
      dynamicImportFn()
        .then((component) => {
          // no refresh needed
          sessionStorage.setItem(HAS_REFRESHED_KEY, 'false');
          resolve(component);
        })
        .catch((error) => {
          // eslint-disable-next-line no-undef
          if (!hasRefreshed && process.env.NODE_ENV !== 'development') {
            setTimeout(handleRefresh, 3000);
          }
          reject(error);
        });
    });
  });

/**
 * Prevents wheel events from changing the numeric input value
 * @function
 * @param {React.WheelEvent} e - The wheel event
 * @returns {void}
 */
export const handleWheel = (e) => {
  // Prevent the input value change on scroll
  e.target.blur();
};

/**
 * @function scrollToBottom
 * @description Scrolls the containerRef to the bottom with specified behavior.
 * @param {string} [behavior='instant'] - The scroll behavior ('auto', 'smooth', or 'instant').
 * @returns {void}
 */
export const scrollToBottom = (containerRef, behavior = 'instant') => {
  if (containerRef.current) {
    containerRef.current.scroll({
      top: containerRef.current.scrollHeight,
      behavior,
    });
  }
};

/**
 * Copies the given text to the clipboard.
 *
 * @param {string} text - The text to be copied.
 * @return {Promise<void>} - A promise that resolves when the text is successfully copied.
 */
export const copyToClipboard = async (text) => {
  try {
    await window.navigator.clipboard.writeText(text);
    SwalToast({
      icon: 'success',
      title: `Copied to clipboard.`,
    });
  } catch {
    SwalToast({
      icon: 'error',
      title: `Failed to copy to clipboard.`,
    });
  }
};

export const downloadBlobResponse = (response, fileName) => {
  const blob = new Blob([response?.data ?? response]);
  const url = window.URL.createObjectURL(blob);
  const anchorElement = document.createElement('a');
  anchorElement.href = url;
  anchorElement.download = fileName;
  document.body.appendChild(anchorElement);
  anchorElement.click();
  document.body.removeChild(anchorElement);
  window.URL.revokeObjectURL(url);
};

export const transformGridLayoutArray = (gridLayout) => {
  const len = gridLayout.length;
  let sum = 0;
  const ans = Array.from({ length: len }, () => 0);
  let pointer = 0;
  let i;

  for (i = 0; i < len; i += 1) {
    sum += gridLayout[i];

    if (sum === 12) {
      let j;
      for (j = pointer; j <= i; j += 1) {
        ans[j] = gridLayout[j];
      }
      pointer = j;
      sum = 0;
    }

    if (sum > 12) {
      const element = 12 / (i - pointer);
      let j;
      for (j = pointer; j < i; j += 1) {
        ans[j] = Math.floor(element);
      }
      pointer = j;
      sum = 0;
      i -= 1; // NOSONAR
    }
  }

  if (pointer < len) {
    const element = 12 / (i - pointer);
    let j;
    for (j = pointer; j < i; j += 1) {
      ans[j] = Math.floor(element);
    }
  }

  return ans;
};
